import React, { lazy, Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { $t } from "~/i18n.js";

import {
  EnvironmentOutlined,
  PrinterOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { Button, Input, Radio, Switch, Table } from "antd";

import { MainLayout } from "~/components/layout/layout.jsx";
import { UserModal } from "~/components/profile/userModal.jsx";
import { api } from "~/lib/api.js";
import { daysActions, flightsActions } from "~/redux/actions.js";
import { InstructeurLijst, ledenLijst } from "./table.columns.jsx";

import { toLocaleDate } from "~/lib/localize.js";
import { EmailComponent } from "./email.component.jsx";

import { message, Spin } from "antd";
import { useMedia } from "use-media";
import { RefreshBaseData } from "~/components/buttons/RefreshBaseData.jsx";
import { CompetencySelector } from "~/components/form/CompetencySelector.jsx";
import { BreadcrumbHeader } from "~/components/layout/breadcrumbHeader.jsx";
import { defaultTableConfig } from "~/components/layout/table.jsx";
import { clubHasFlight } from "~/featureMatrix.jsx";
import { selectElementByID } from "~/lib/helpers.js";

const UserMapView = lazy(() => import("~/components/userMap/UserMapView.jsx"));

export const LedenLijstOverzichtPagina = ({ history, match }) => {
  const [vliegers, setVliegers] = useState([]);

  const dispatch = useDispatch();
  const persist = useSelector((state) => state.persist);
  const flights = useSelector((state) => state.flights);
  const days = useSelector((state) => state.days);

  const getBaseData = () => dispatch(flightsActions.getBaseData());
  const getAllDays = () => dispatch(daysActions.getAllDays());

  useEffect(() => searchVlieger(""), [flights.vliegers.length]);
  useEffect(() => setColumns(), []);

  const [search, setSearch] = useState("");

  const isWide = useMedia({ minWidth: "450px" }, false);

  const searchVlieger = (searchText) => {
    const newSearch = searchText?.toLowerCase();

    // filter out passengers, negative ID's
    let vliegers = flights.vliegers?.filter((item) => item.id > 0);
    if (!vliegers) return null;

    if (newSearch) {
      vliegers = vliegers.filter(
        (vlieger) => vlieger.lowercase.indexOf(newSearch) > -1,
      );
    }
    setVliegers(vliegers);
  };

  const [selectedCompetency, setSelectedCompetency] = useState([]);
  const [searchVliegers, setSearchVliegers] = useState(false);
  const [selectedVliegerIDs, setSelectedVliegerIDs] = useState([]);

  const searchCompetency = async (cascaderValue) => {
    setSelectedCompetency(cascaderValue);
    if (cascaderValue.length === 3) {
      setSearchVliegers(true);
      const data = await api.get(
        `gegevens/${cascaderValue[2]}/filter_competencies.json`,
      );
      if (data?.length === 0) message.info($t("Geen vliegers gevonden"));
      setSelectedVliegerIDs(data || []);
      setSearchVliegers(false);
    } else {
      setSelectedVliegerIDs([]);
      searchVlieger(search);
    }
  };

  const groups_exclude = persist.club?.groups_exclude
    ? persist.club.groups_exclude
        .split(",")
        .map((g) => g.trim())
        .filter(Boolean)
    : [];

  const [showHidden, setShowHidden] = useState(false);

  const allListVliegers =
    selectedVliegerIDs.length > 0
      ? vliegers.filter((v) => selectedVliegerIDs.includes(v.id))
      : vliegers;

  const nHidden =
    groups_exclude.length > 0
      ? allListVliegers.filter((v) =>
          v.group_keys?.some((k) => groups_exclude.includes(k)),
        ).length
      : 0;

  const listVliegers =
    showHidden || groups_exclude.length === 0
      ? allListVliegers
      : allListVliegers.filter(
          (v) => !v.group_keys?.some((k) => groups_exclude.includes(k)),
        );

  const [tableColumns, setStateColumns] = useState();
  const [loadingFile, setFileLoading] = useState(false);
  const [viewMode, setViewMode] = useState("list");

  // using router to navigate opened pilot
  const [openedPilot, setOpenPilot] = useState(null);
  useEffect(() => {
    setOpenPilot({
      id: match.params.id,
      ...selectElementByID(
        flights.vliegers,
        "id",
        parseInt(match.params.id || 0),
      ),
    });
  }, [match.params.id]);

  const setColumns = (newNotities, showInstructorColumns) => {
    const selectedNotities = newNotities === undefined ? notities : newNotities;
    const showIntColumns =
      showInstructorColumns === undefined
        ? showInstructorInfo
        : showInstructorColumns;

    const groups_filter = persist.club && persist.club.groups_filter;
    const group_name_lookup = flights?.group_name_lookup || {};

    // set column to render table
    let columns = [];

    if (showIntColumns) {
      columns = InstructeurLijst(
        selectedNotities,
        groups_filter,
        group_name_lookup,
        history,
        clubHasFlight(),
        persist.club?.association === "knvvl",
      );
    } else {
      columns = ledenLijst(
        groups_filter,
        group_name_lookup,
        history,
        clubHasFlight(),
      );
    }

    // we push the columns to a state to update the table
    setStateColumns(columns);
  };

  // loading in all data, flights, days and dto information
  useEffect(() => {
    const loadData = async () => {
      // load in base data if needed
      if (!flights.lastUpdatedBaseData) await getBaseData();
      if (!days.lastUpdated) await getAllDays();
    };

    // when opening
    loadData();
  }, [flights.lastUpdatedBaseData, days.lastUpdated]);

  const [showInstructorInfo, setShowInstructorInfo] = useState(false);
  const [instructorInfoLoading, setInstructorInfoLoading] = useState(false);
  const loadingData =
    instructorInfoLoading || flights.isPending || days.isPending;

  const [notities, setNotities] = useState({});
  const getInstructorInfo = async () => {
    setShowInstructorInfo(true);
    setInstructorInfoLoading(true);
    const res = await api.get("instructie/notities.json");
    if (res) {
      setNotities(res.notities);
      setColumns(res.notities, true);
    }
    setInstructorInfoLoading(false);
  };
  const TurnInstructorInfoOff = () => {
    setShowInstructorInfo(false);
    setColumns(notities, false);
  };

  return (
    <>
      <UserModal
        link={window.location.href}
        visible={openedPilot && openedPilot.id}
        onClose={() => history.replace("/members")}
        pilot={openedPilot}
        newNotities={(_new) => {
          notities[openedPilot.id] = _new;
          setNotities(notities);
          setColumns();
        }}
      />

      <MainLayout history={history}>
        <BreadcrumbHeader
          breadcrumbs={[$t("Ledenlijst")]}
          buttons={[
            <RefreshBaseData
              key="1"
              style={{ marginRight: 8 }}
              extra={() => {
                if (showInstructorInfo) getInstructorInfo();
              }}
            />,

            isWide && persist.profile.ziet_emaillijst && <EmailComponent />,
            isWide && persist.profile.wijzig_dto && (
              <Button
                icon={<PrinterOutlined />}
                loading={loadingFile}
                size="small"
                onClick={async () => {
                  setFileLoading(true);
                  await api.open_file(
                    `bevoegdheid/members/export.json`,
                    `${$t("bevoegheden")} ${toLocaleDate(new Date())}.xlsx`,
                  );
                  setFileLoading(false);
                }}
              >
                {$t("Exporteer")}
              </Button>
            ),

            !persist?.profile?.private_mode && (
              <Radio.Group
                key="view-toggle"
                size="small"
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
                options={[
                  { value: "list", label: <TableOutlined /> },
                  { value: "map", label: <EnvironmentOutlined /> },
                ]}
                optionType="button"
                buttonStyle="solid"
              />
            ),
          ]}
        />

        <div className="column" style={{ alignItems: "flex-start" }}>
          <div
            className="row"
            style={{
              gap: 12,
              marginTop: 6,
              flexWrap: "wrap",
              marginBottom: 6,
              width: "100%",
            }}
          >
            <Input.Search
              style={{ maxWidth: 250 }}
              value={search}
              disabled={loadingData}
              size="small"
              placeholder={$t("Zoek op naam")}
              onChange={(event) => {
                const searchValue = event.target.value;
                setSearch(searchValue);
                searchVlieger(searchValue);
              }}
            />

            <CompetencySelector
              size="small"
              style={{ minWidth: 200, maxWidth: 300 }}
              value={selectedCompetency}
              onChange={(v) => searchCompetency(v)}
              loading={searchVliegers}
              onClear={
                selectedCompetency?.length > 0 || search?.length > 0
                  ? () => {
                      setSelectedCompetency([]);
                      setSelectedVliegerIDs([]);
                      setSearch("");
                      searchVlieger("");
                    }
                  : null
              }
            />

            {persist.profile.wijzig_leden && nHidden > 0 && (
              <div className="row" style={{ gap: 6 }}>
                <Switch
                  size="small"
                  checked={showHidden}
                  onChange={setShowHidden}
                />
                <span className="small">
                  {$t("Toon verborgen gebruikers")} ({nHidden})
                </span>
              </div>
            )}
          </div>

          {persist.profile.ziet_dto && (
            <p className="spacer">
              {$t("Bekijk instructie informatie:")}
              <Switch
                size="small"
                loading={instructorInfoLoading}
                style={{ marginLeft: 4 }}
                onChange={(checked) =>
                  checked ? getInstructorInfo() : TurnInstructorInfoOff()
                }
                checked={showInstructorInfo}
              />
            </p>
          )}

          {viewMode === "map" ? (
            <Suspense fallback={<Spin style={{ margin: 24 }} />}>
              <UserMapView listVliegers={listVliegers} history={history} club={persist.club} />
            </Suspense>
          ) : (
            <Table
              loading={loadingData || searchVliegers}
              style={{
                marginTop: 12,
                width: "95vw",
                overflowX: "auto",
                cursor: "pointer",
              }}
              pagination={{ ...defaultTableConfig, defaultPageSize: 100 }}
              size="small"
              rowKey="id"
              dataSource={listVliegers}
              columns={tableColumns}
            />
          )}
        </div>
      </MainLayout>
    </>
  );
};
